import { DatePicker } from "@heroui/react";
import { CalendarDate, parseDate } from "@internationalized/date";
import React from "react";
import { useTranslation } from "react-i18next";
import { UpdateProfileUIProps } from "../../../types/updateProfileProps";
import { ContriesDropdown } from "../../common/countriesDropdown";
import { FieldError, FieldLabel, SectionTitle, SelectField, TextField } from "./formPrimitives";

const GENDERS = ["MALE", "FEMALE", "OTHER"];

export const safeParseDate = (value?: string | null): CalendarDate | null => {
    if (!value) return null;

    const datePart = value.split("T")[0];
    if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return null;

    try {
        return parseDate(datePart);
    } catch {
        return null;
    }
};

type FigmaDatePickerProps = {
    name: string;
    label: string;
    value: string;
    required?: boolean;
    error?: string;
    maxToday?: boolean;
    onValueChange: (value: string) => void;
};

/** Figma date field: grey fill, "mm / dd / yyyy" segments, calendar icon */
export const FigmaDatePicker: React.FC<FigmaDatePickerProps> = ({
    name,
    label,
    value,
    required,
    error,
    maxToday,
    onValueChange,
}) => {
    const now = new Date();
    const today = new CalendarDate(now.getFullYear(), now.getMonth() + 1, now.getDate());

    return (
        <div className="flex w-full flex-col gap-1">
            <FieldLabel required={required}>{label}</FieldLabel>
            <DatePicker
                name={name}
                aria-label={label}
                value={safeParseDate(value)}
                onChange={(date) => onValueChange(date ? date.toString() : "")}
                maxValue={maxToday ? today : undefined}
                isInvalid={!!error}
                classNames={{
                    base: "w-full",
                    inputWrapper:
                        "h-9 min-h-9 rounded-xl bg-[#EBEBEC] px-3 shadow-none data-[hover=true]:bg-[#EBEBEC] group-data-[focus=true]:bg-white",
                    input: "text-sm text-gray-900",
                    segment: "text-sm",
                }}
            />
            <FieldError message={error} />
        </div>
    );
};

/** Figma "Personal details" section */
export const PersonalDetailsSection: React.FC<UpdateProfileUIProps> = ({
    form,
    setField,
    errors,
    isFighter,
    isOrganization,
}) => {
    const { t } = useTranslation("updateProfile");

    const genderOptions = GENDERS.map((gender) => ({
        value: gender,
        label: t(`page.genders.${gender}`),
    }));

    return (
        <section id="section-personal-details" className="flex flex-col gap-4">
            <SectionTitle>{t("page.personalDetails")}</SectionTitle>

            <TextField
                name="firstName"
                label={t("page.firstName")}
                required
                maxLength={30}
                value={form.firstName}
                placeholder={t("page.firstNamePlaceholder")}
                onValueChange={(value) => setField("firstName", value)}
                error={errors.firstName}
            />
            <TextField
                name="lastName"
                label={t("page.lastName")}
                required
                maxLength={25}
                value={form.lastName}
                placeholder={t("page.lastNamePlaceholder")}
                onValueChange={(value) => setField("lastName", value)}
                error={errors.lastName}
            />

            <SelectField
                name="gender"
                label={t("page.gender")}
                required
                value={form.gender}
                placeholder={t("page.genderPlaceholder")}
                options={genderOptions}
                onValueChange={(value) => setField("gender", value)}
                error={errors.gender}
            />

            {isOrganization && (
                <TextField
                    name="organizationName"
                    label={t("page.organizationName")}
                    required
                    maxLength={40}
                    value={form.organizationName}
                    placeholder={t("page.enterOrganizationName")}
                    onValueChange={(value) => setField("organizationName", value)}
                    error={errors.organizationName}
                />
            )}

            {isFighter && (
                <>
                    <TextField
                        name="nickname"
                        label={t("page.nickname")}
                        maxLength={25}
                        value={form.nickname}
                        placeholder={t("page.nicknamePlaceholder")}
                        onValueChange={(value) => setField("nickname", value)}
                        error={errors.nickname}
                    />
                    <FigmaDatePicker
                        name="dateOfBirth"
                        label={t("page.dateOfBirth")}
                        required
                        maxToday
                        value={form.dateOfBirth}
                        onValueChange={(value) => setField("dateOfBirth", value)}
                        error={errors.dateOfBirth}
                    />
                </>
            )}
        </section>
    );
};

/** Figma "Location details" section (nationality + residence country) */
export const LocationDetailsSection: React.FC<UpdateProfileUIProps> = ({
    form,
    setField,
    errors,
    countries,
}) => {
    const { t } = useTranslation("updateProfile");

    return (
        <section id="section-location-details" className="flex flex-col gap-4">
            <SectionTitle>{t("page.locationDetails")}</SectionTitle>

            <div className="flex w-full flex-col gap-1">
                <FieldLabel required>{t("page.nationality")}</FieldLabel>
                <ContriesDropdown
                    variant="figma"
                    strict
                    options={countries}
                    value={form.nationality}
                    placeholder={t("page.nationalityPlaceholder")}
                    hasError={!!errors.nationality}
                    onLocationChange={(value) => setField("nationality", value)}
                />
                <FieldError message={errors.nationality} />
            </div>

            <div className="flex w-full flex-col gap-1">
                <FieldLabel required>{t("page.residenceCountry")}</FieldLabel>
                <ContriesDropdown
                    variant="figma"
                    strict
                    options={countries}
                    value={form.residenceCountry}
                    placeholder={t("page.residenceCountryPlaceholder")}
                    hasError={!!errors.residenceCountry}
                    onLocationChange={(value) => setField("residenceCountry", value)}
                />
                <FieldError message={errors.residenceCountry} />
            </div>
        </section>
    );
};
